import {
	type CompanyContactId,
	ContractSuretyType,
	type Dtos,
	type NaicsCodeId,
	type SuretyAccountId,
} from "@inrev/common";
import { useFetchAccountCompanies, useFetchSuretyAccount } from "@portal/api/account";
import { useFetchAccountSpecialtyCodes } from "@portal/api/account/agent";
import { type SuretyBond, useFetchBond } from "@portal/api/bond/agent";
import { useCreateBondRequest } from "@portal/api/bond-request/agent";
import { SectionNavLayout } from "@portal/components/multiform/SectionNavLayout";
import type { StepConfig } from "@portal/components/multiform/types";
import { createFormResolver } from "@portal/components/multiform/utils/formResolver";
import { CenteredSpinner } from "@portal/components/ui/CenteredSpinner";
import {
	bondRequestOutcomeFromStatus,
	useBondRequestFlowAnalytics,
} from "@portal/hooks/useBondRequestFlowAnalytics";
import { useIsMinimalBondUIEnabled } from "@portal/hooks/useIsMinimalBondUIEnabled";
import { useAuthenticatedUser } from "@portal/providers/AuthenticatedUserProvider";
import { FormSchemaProvider } from "@portal/providers/FormSchemaProvider";
import { createContext, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import type { BondFormData } from "../../../../types";
import {
	type BondRequestCreateFormData,
	getBondRequestFormValidationSchema,
	getDefaultBondRequestFormData,
	transformBondFormForSubmission,
} from "./schema";
import { additionalInfoStepConfig } from "./steps/additional-info/AdditionalInfo.config";
import { bondStepConfig } from "./steps/bond/Bond.config";

const defaultBondFormData: BondFormData = {
	templateId: "",
	upload: { type: "", files: [] },
};

export type PrincipalCompanyOption = {
	id?: CompanyContactId;
	name: string;
	isPrimary: boolean;
};

export const BondRequestCreateContext = createContext<{
	accountNaicsCodes?: NaicsCodeId[];
	bondFormData: BondFormData;
	setBondFormData: (data: BondFormData) => void;
	companies?: PrincipalCompanyOption[];
	bidBondPrincipalId?: CompanyContactId;
}>({
	accountNaicsCodes: undefined,
	bondFormData: defaultBondFormData,
	setBondFormData: () => {},
});

export const BondRequestCreateView = () => {
	const [searchParams] = useSearchParams();
	const accountId = searchParams.get("accountId") as SuretyAccountId | null;
	const bidBondId = searchParams.get("bidBondId");

	if (!accountId) {
		return <Navigate to="/accounts" replace />;
	}

	return <BondRequestCreateViewWithAccount accountId={accountId} bidBondId={bidBondId} />;
};

const BondRequestCreateViewWithAccount = ({
	accountId,
	bidBondId,
}: {
	accountId: SuretyAccountId;
	bidBondId: string | null;
}) => {
	const { suretyAccount, suretyAccountIsLoading } = useFetchSuretyAccount(accountId);
	const { bond: bidBond, bondLoading: bidBondLoading } = useFetchBond(
		bidBondId ?? "",
		bidBondId !== null,
	);
	const { companies: accountCompanies, companiesIsLoading } = useFetchAccountCompanies(accountId);
	const { rows: specialtyRows, isLoading: specialtyIsLoading } =
		useFetchAccountSpecialtyCodes(accountId);

	// The scope-of-work picker keys its subset filter off account specialties at mount, so the
	// form must not render until companies and specialties have resolved.
	if (
		suretyAccountIsLoading ||
		!suretyAccount ||
		companiesIsLoading ||
		!accountCompanies ||
		specialtyIsLoading ||
		(bidBondId && (bidBondLoading || !bidBond))
	) {
		return <CenteredSpinner />;
	}

	return (
		<BondRequestCreateForm
			suretyAccount={suretyAccount}
			bidBond={bidBond}
			bidBondId={bidBondId}
			accountCompanies={accountCompanies}
			accountNaicsCodes={specialtyRows.map((row) => row.id)}
		/>
	);
};

const BondRequestCreateForm = ({
	suretyAccount,
	bidBond,
	bidBondId,
	accountCompanies,
	accountNaicsCodes,
}: {
	suretyAccount: NonNullable<ReturnType<typeof useFetchSuretyAccount>["suretyAccount"]>;
	bidBond: SuretyBond | undefined;
	bidBondId: string | null;
	accountCompanies: (Dtos.Company.Get.Response | Dtos.Admin.SuretyAccount.Get.Response.Company)[];
	accountNaicsCodes: NaicsCodeId[];
}) => {
	const navigate = useNavigate();
	const { user } = useAuthenticatedUser();
	const { createBondRequest, createBondRequestIsLoading } = useCreateBondRequest();

	const isMinimalUIEnabled = useIsMinimalBondUIEnabled();
	const bondRequestFormValidationSchema = useMemo(
		() => getBondRequestFormValidationSchema({ minimalMode: isMinimalUIEnabled }),
		[isMinimalUIEnabled],
	);
	const steps: StepConfig<BondRequestCreateFormData>[] = useMemo(
		() => (isMinimalUIEnabled ? [bondStepConfig] : [bondStepConfig, additionalInfoStepConfig]),
		[isMinimalUIEnabled],
	);

	const companies = useMemo((): PrincipalCompanyOption[] => {
		return accountCompanies.map((c) => ({
			id: c.id,
			name: c.name,
			isPrimary: c.id === suretyAccount.primaryCompanyId,
		}));
	}, [accountCompanies, suretyAccount.primaryCompanyId]);

	const [bondFormData, setBondFormData] = useState<BondFormData>(defaultBondFormData);
	const bondFormRef = useRef(bondFormData);
	bondFormRef.current = bondFormData;

	const analytics = useBondRequestFlowAnalytics("page");

	const defaultValues = useMemo(() => {
		const contractSuretyType = bidBondId ? ContractSuretyType.bid_to_final : undefined;

		const overrides: Record<string, unknown> = {};

		if (user?.type === "agent") {
			overrides.agencyId = user.agencyId;
		}

		if (bidBondId) {
			overrides.bidBondId = bidBondId;
		}
		if (bidBond?.contractSuretyType === ContractSuretyType.bid) {
			overrides.actualBidDate = bidBond.bidDate;
		}

		return getDefaultBondRequestFormData(contractSuretyType, overrides);
	}, [user, bidBondId, bidBond]);

	const formMethods = useForm<BondRequestCreateFormData>({
		mode: "onBlur",
		resolver: createFormResolver(bondRequestFormValidationSchema),
		defaultValues,
	});

	const { onEntered, onCreated, onOutcomeShown, onFailed, onAbandoned } = analytics;

	useEffect(() => {
		onEntered(suretyAccount.id);
	}, [suretyAccount.id, onEntered]);

	useEffect(() => {
		const bondForm = transformBondFormForSubmission(bondFormData);
		formMethods.setValue("quote.bondForm", bondForm ?? null);
	}, [bondFormData, formMethods]);

	const bidBondPrincipalId = useMemo(() => {
		if (!bidBond?.principalId) return undefined;
		const matching = companies.find((c) => c.id === bidBond.principalId);
		return matching?.id;
	}, [bidBond, companies]);

	return (
		<BondRequestCreateContext.Provider
			value={{
				accountNaicsCodes,
				bondFormData,
				setBondFormData,
				companies,
				bidBondPrincipalId,
			}}
		>
			<FormProvider {...formMethods}>
				<FormSchemaProvider value={bondRequestFormValidationSchema}>
					<SectionNavLayout
						formMethods={formMethods}
						steps={steps}
						isSubmitting={createBondRequestIsLoading}
						onSuccess={async (data) => {
							try {
								const submissionData = {
									...data,
									quote: {
										...data.quote,
										bondForm: transformBondFormForSubmission(bondFormRef.current),
									},
								};
								const result = await createBondRequest({
									accountId: suretyAccount.id,
									data: submissionData,
								});
								onCreated(suretyAccount.id, result.id);
								const outcome = bondRequestOutcomeFromStatus(result.status);
								if (outcome) onOutcomeShown(suretyAccount.id, result.id, outcome);
								navigate(`/accounts/${suretyAccount.id}/requests/${result.id}`);
							} catch (error) {
								onFailed(suretyAccount.id, error instanceof Error ? error.name : "Unknown error");
								throw error;
							}
						}}
						onAbandon={onAbandoned}
						confirmationMessage="Are you sure you want to submit this bond request?"
						trackingPrefix="bond-request-page"
					/>
				</FormSchemaProvider>
			</FormProvider>
		</BondRequestCreateContext.Provider>
	);
};
